import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import { Grid, Button, IconButton, Divider, Drawer, Hidden, Badge } from '@material-ui/core';
import ReactFlagsSelect from 'react-flags-select';
import NotificationsIcon from '@material-ui/icons/NotificationsRounded';
import AccountCircle from '@material-ui/icons/AccountCircle';
import ExitToAppRounded from '@material-ui/icons/ExitToAppRounded';
import { push } from 'connected-react-router';

import { FEATURE_FLAG_NAMES } from 'yeself-common/typings/FeatureFlags';

import { authLogoutRequest } from 'yeself-client-common/security/SecurityAction';

import { selectAccountUrl } from 'yeself-client-common/security/SecuritySelector';

import Feature from 'yeself-client-common/featureFlags/components/Feature';

import { fetchMemberOrganizationsList } from 'Console/data/organizations/OrganizationsAction';

import { selectMemberOrganizationsList } from 'Console/data/organizations/OrganizationSelector';

import AnalyticsButton from 'Console/components/common/analyticsButton/AnalyticsButton';

import { selectUserEmail } from 'Console/data/security/selectors';
import { ROUTES, ACCOUNT_DETAIL_ROUTE, ANALYTICS_ROUTE } from 'Console/constants/routes';
import { changeConsoleLanguage } from 'Console/data/app/actions';
import { selectAvailableLanguages, selectConsoleLanguageCode } from 'Console/data/app/selectors';
import { mapLanguageCodeToCountryCode, mapCountryCodeToLanguage } from 'Console/utils/translationLoader';

import 'react-flags-select/css/react-flags-select.css';

const drawerWidth = 240;

const styles = (theme) => ({
    root: {
        display: 'flex',
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
    },
    toolbar: theme.mixins.toolbar,
    drawerPaper: {
        width: '100vw',
    },
    menuBox: {
        padding: 15,
    },
    navLink: {
        textDecoration: 'none',
        color: 'black',
    },
    titleIcon: {
        fontSize: theme.typography.pxToRem(14),
        paddingLeft: 9,
    },
    menuFlags: {
        padding: 12,
    },
});

const MobileDrawer = ({
    classes,
    mobileMenuOpened,
    handleDrawerToggle,
    availableLocales,
    consoleLanguageCode,
    accountId,
    accountUrl,
    authLogoutRequest,
    changeConsoleLanguage,
    container,
    memberOrganizationsList,
    push,
    ...props
}) => {
    const onMobileDrawerClose = () => handleDrawerToggle(false);
    const handleChangeConsoleLanguage = (countryCode) => {
        const selectedLocale = availableLocales.find(
            (locale) => locale.code.toLowerCase() === mapCountryCodeToLanguage(countryCode).toLowerCase()
        );
        changeConsoleLanguage(selectedLocale);
        handleDrawerToggle(false);
    };

    const onOrganizationClick = (organizationId) => {
        push(`${ANALYTICS_ROUTE}${organizationId}`);
        handleDrawerToggle(false);
    };

    const drawer = (
        <div>
            <div className={classes.toolbar} />
            <Divider />
            <Grid container direction="column" alignItems="center" className={classes.menuBox}>
                <NavLink
                    className={classes.navLink}
                    activeStyle={{ color: '#9BAE00' }}
                    to={ROUTES.BASE}
                    onClick={onMobileDrawerClose}
                    exact
                >
                    <Button id="yeself-button" size="small" color="inherit">
                        <span className={classes.label}>
                            <FormattedMessage id="Navigation.applications" />
                        </span>
                    </Button>
                </NavLink>
            </Grid>

            <Divider />

            <Grid container direction="column" alignItems="center" className={classes.menuBox}>
                <Feature name={FEATURE_FLAG_NAMES.ANALYTICS_CONSOLE}>
                    <AnalyticsButton
                        onOrganizationItemClick={onOrganizationClick}
                        organizations={memberOrganizationsList}
                    />
                </Feature>
            </Grid>

            <Divider />

            <Grid container justify="center" className={classes.menuBox}>
                <Feature name={FEATURE_FLAG_NAMES.CONSOLE_LANGUAGE}>
                    <ReactFlagsSelect
                        className={classes.menuFlags}
                        countries={availableLocales.map((locale) =>
                            mapLanguageCodeToCountryCode(locale.code).toUpperCase()
                        )}
                        defaultCountry={mapLanguageCodeToCountryCode(consoleLanguageCode).toUpperCase()}
                        showSelectedLabel
                        showOptionLabel
                        selectedSize={14}
                        onSelect={handleChangeConsoleLanguage}
                    />
                </Feature>

                <Feature name={FEATURE_FLAG_NAMES.NOTIFICATIONS}>
                    <IconButton color="inherit">
                        <NavLink
                            className={classes.navLink}
                            activeStyle={{ color: '#9BAE00' }}
                            to={`${ACCOUNT_DETAIL_ROUTE}${accountId}/notifications`}
                            onClick={onMobileDrawerClose}
                            exact
                        >
                            <Badge badgeContent={4} color="secondary">
                                <NotificationsIcon />
                            </Badge>
                            <span className={classes.titleIcon}>
                                <FormattedMessage id="AccountNotifications.title" />
                            </span>
                        </NavLink>
                    </IconButton>
                </Feature>

                <Feature name={FEATURE_FLAG_NAMES.PROFILE}>
                    <IconButton aria-haspopup="true" color="inherit" href={accountUrl}>
                        <AccountCircle />
                        <span className={classes.titleIcon}>
                            <FormattedMessage id="Navigation.user.profile" />
                        </span>
                    </IconButton>
                </Feature>

                <IconButton
                    aria-haspopup="true"
                    color="inherit"
                    className={classes.navLink}
                    onClick={authLogoutRequest}
                >
                    <ExitToAppRounded />
                    <span className={classes.titleIcon}>
                        <FormattedMessage id="Navigation.logout" />
                    </span>
                </IconButton>
            </Grid>
        </div>
    );

    return (
        <nav className={classes.drawer}>
            {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
            <Hidden smUp implementation="css">
                <Drawer
                    container={container}
                    variant="temporary"
                    anchor="top"
                    open={mobileMenuOpened}
                    onClose={onMobileDrawerClose}
                    classes={{
                        paper: classes.drawerPaper,
                    }}
                >
                    {drawer}
                </Drawer>
            </Hidden>
        </nav>
    );
};

export default connect(
    (state) => ({
        availableLocales: selectAvailableLanguages(state),
        consoleLanguageCode: selectConsoleLanguageCode(state),
        accountUrl: selectAccountUrl(state),
        accountId: selectUserEmail(state),
        memberOrganizationsList: selectMemberOrganizationsList(state),
    }),
    {
        authLogoutRequest,
        changeConsoleLanguage,
        push,
    }
)(withStyles(styles, { withTheme: true })(MobileDrawer));
